import React from "react";

import { useSettings } from "@/shared/api/settings";
import { openWorkspaceSettings } from "@/shared/lib/openWorkspaceSettings";

/* Org / Section / Page — where a workspace page sits: the org on a grey tile that opens
   Settings, bold slash bars, each step back quiet with a hover fill, the page in ink. */

export const crumbRowCls = "min-w-0 flex items-center gap-0.5";
/** A step back: quiet, with the row hover fill. */
export const crumbCls =
  "press shrink-0 min-w-0 max-w-[220px] h-7 px-1.5 rounded-[8px] flex items-center gap-1.5 text-[13.5px] tracking-[-0.005em] text-[var(--text-tertiary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]";
/** The open page: plain ink, no tile. */
export const currentCrumbCls =
  "shrink-0 min-w-0 max-w-[260px] h-7 px-1.5 flex items-center gap-1.5 text-[13.5px] font-medium tracking-[-0.005em] text-[var(--text-primary)]";

export const CrumbSlash = () => <span aria-hidden="true" className="shrink-0 mx-1.5 w-[2px] h-3.5 rounded-full bg-[var(--text-quaternary)] -skew-x-[16deg]" />;

/** The org's tile and the slash after it; nothing until `/settings` has answered. */
export function OrgCrumb({ className = "" }: { className?: string }) {
  const name = useSettings().data?.org?.name;
  if (!name) return null;
  return (
    <span className={`contents ${className}`}>
      <button
        onClick={() => openWorkspaceSettings("general")}
        title={`${name} · Settings`}
        className="press shrink-0 min-w-0 max-w-[180px] h-7 px-2.5 rounded-[8px] flex items-center bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[13.5px] font-medium tracking-[-0.005em] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
      >
        <span className="truncate">{name}</span>
      </button>
      <CrumbSlash />
    </span>
  );
}

/** `children` is the page; `parent` the section it opened from. A phone drops the org when there is one. */
export function Breadcrumb({ parent, children }: {
  parent?: { label: string; onClick: () => void };
  children: React.ReactNode;
}) {
  return (
    <nav aria-label="Breadcrumb" className={crumbRowCls}>
      <OrgCrumb className={parent ? "max-sm:hidden" : ""} />
      {parent && (
        <>
          <button onClick={parent.onClick} className={crumbCls}>{parent.label}</button>
          <CrumbSlash />
        </>
      )}
      <span aria-current="page" className={currentCrumbCls}>{children}</span>
    </nav>
  );
}
